import { Platform } from "quasar";
import { useRouter } from "vue-router";
import { useCommonStore } from "src/stores/common-store";
import { useNotificationDetails } from "src/stores/notification-store";
import { usePersistedStore } from "src/stores/persisted-store";
import { getAccountID } from "src/utils/commonFunction";

/**
 * Single source of truth for push-notification redirection, shared by the
 * native (Cordova) flow in App.vue and the web/PWA flow in
 * HandlePushNotificationPWA.vue.
 *
 * Key robustness rule: the payload lives in `persistedStore.pendingNotification`
 * (persisted to localStorage), NOT in a volatile in-memory variable or the URL
 * query. It is cleared ONLY after the target navigation has actually resolved,
 * so a full reload mid-startup simply retries the same redirect instead of
 * dropping it.
 */
export function useNotificationRedirection() {
  const router = useRouter();
  const commonStore = useCommonStore();
  const notificationStore = useNotificationDetails();
  const persistedStore = usePersistedStore();

  // Per-instance in-flight guard. The mobile timer reads this to avoid firing
  // a second redirect while one is already running.
  let loadingApi = false;
  const isLoading = () => loadingApi;

  // Read the durable payload. Empty object means "nothing pending".
  const getPayload = () => persistedStore.pendingNotification || {};

  // Store a fresh payload durably (used on arrival / on page mount).
  const setPayload = (payload) => {
    persistedStore.pendingNotification =
      payload && Object.keys(payload).length ? { ...payload } : null;
  };

  const hasPending = () => Object.keys(getPayload()).length > 0;

  // Push campaign open tracking — called at tap time, not at redirect time, so
  // the open is counted even when the user is logged out (the API is open).
  const trackOpen = (payload) => {
    notificationStore.trackPushCampaignOpen(payload);
  };

  // Clear ONLY after a successful navigation so reloads can retry safely.
  const clearPending = () => {
    loadingApi = false;
    commonStore.setLoader(false);
    persistedStore.pendingNotification = null;
  };

  // Navigate then clear the pending payload once the push settles. If the push
  // rejects we still clear to avoid an infinite redirect loop.
  const go = (location) => {
    Promise.resolve(router.push(location))
      .catch(() => {})
      .finally(() => clearPending());
  };

  function sendToHome() {
    if (Platform?.is?.mobile) {
      go({ name: "Home" });
    } else {
      go({ name: "Home", query: { openNotification: true } });
    }
  }

  function seeCoupon(notificationObj) {
    let objtmp = null;
    if (notificationObj?.full_description) {
      try {
        objtmp = JSON.parse(notificationObj.full_description);
      } catch (e) {
        objtmp = null;
      }
    }

    if (
      objtmp?.type == "manufacturer" &&
      objtmp?.manufacturer_account_id?.length > 0
    ) {
      // Select the store context first (web already did this; mobile now matches).
      if (notificationObj?.account_id) {
        commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
      }
      go({
        name: "MyMFGDetail",
        params: { manufacturerid: objtmp.manufacturer_account_id, from: "Mfg" },
        query: { tab: "Coupons" },
      });
    } else if (notificationObj?.account_id?.length > 0) {
      commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
      if (Platform?.is?.mobile) {
        go({
          name: "MyCoupons",
          params: { storeid: notificationObj.account_id },
        });
      } else {
        go({
          name: "StoreDetail",
          params: { storeid: notificationObj.account_id },
          query: { tab: "Coupons" },
        });
      }
    } else {
      sendToHome();
    }
  }

  function redirectToTokenEarned(notificationObj) {
    let objtmp = null;
    if (notificationObj?.full_description) {
      try {
        objtmp = JSON.parse(notificationObj.full_description);
      } catch (e) {
        objtmp = null;
      }
    }

    const handlePointsBreakdownDetail = (storeId, type) => {
      if (type == "store") {
        commonStore.selectStoreForGlobal({ id: storeId });
      }
      go({ name: "PointsBreakdownDetail", params: { storeid: storeId, type } });
    };

    const handleMyProfile = (storeId) => {
      if (storeId) {
        commonStore.selectStoreForGlobal({ id: storeId });
      }
      go({
        name: "MyProfile",
        query: { tab: "earning", fromnotification: true },
      });
    };

    if (Platform?.is?.mobile) {
      if (
        objtmp?.type === "manufacturer" &&
        objtmp?.manufacturer_account_id?.length > 0
      ) {
        handlePointsBreakdownDetail(objtmp.manufacturer_account_id, "mfg");
      } else if (notificationObj?.account_id?.length > 0) {
        handlePointsBreakdownDetail(notificationObj.account_id, "store");
      } else {
        sendToHome();
      }
    } else {
      if (
        objtmp?.type === "manufacturer" &&
        objtmp?.manufacturer_account_id?.length > 0
      ) {
        if (notificationObj?.account_id) {
          commonStore.selectStoreForGlobal({ id: notificationObj.account_id });
        }
        go({
          name: "MyMFGDetail",
          params: {
            manufacturerid: objtmp.manufacturer_account_id,
            from: "Mfg",
          },
        });
      } else {
        handleMyProfile(notificationObj?.account_id);
      }
    }
  }

  /**
   * A Klaviyo push carries its destination as a URL rather than a typed
   * payload, and the key it lands under depends on how the campaign was built.
   */
  function getKlaviyoUrl(payload) {
    return payload?.url || "";
  }

  /**
   * Percent-encoded URLs arrive when a campaign stores the destination as a
   * query-string value ("https%3A%2F%2F..."). Decode until the separators are
   * real again, so an encoded — or double-encoded — link routes like a plain
   * one. Bails out on malformed sequences rather than throwing.
   */
  function decodeIfEncoded(url) {
    let value = url;

    for (let i = 0; i < 3; i++) {
      // Already usable as-is: an absolute url, a scheme link or a rooted path.
      // Stopping here keeps legitimate escapes inside a path intact.
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith("/"))
        break;
      if (!/%[0-9a-f]{2}/i.test(value)) break;

      try {
        const decoded = decodeURIComponent(value);
        if (decoded === value) break;
        value = decoded;
      } catch (e) {
        break;
      }
    }

    return value;
  }

  /**
   * Path (no query, no hash) out of an absolute URL, a custom-scheme link
   * (lns://store-detail/123/my-deal) or an already-relative path. Returns ""
   * when there is nothing usable, so the caller falls back to the payload ids.
   */
  function extractPath(rawUrl) {
    if (!rawUrl || typeof rawUrl !== "string") return "";

    try {
      const trimmed = decodeIfEncoded(rawUrl.trim());
      if (!trimmed) return "";

      if (/^https?:\/\//i.test(trimmed)) return new URL(trimmed).pathname;

      // Split rather than parse: new URL() lowercases the host, which for a
      // custom scheme is a real path segment we must not alter.
      const path = trimmed
        .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
        .split(/[?#]/)[0];

      return path.startsWith("/") ? path : `/${path}`;
    } catch (e) {
      return "";
    }
  }

  /**
   * The store a Klaviyo push applies to. These payloads carry no account_id or
   * store_id of their own, so the store comes from the build: the loaded store
   * configuration first, then the build-time account id.
   */
  const resolveKlaviyoStoreId = () =>
    getAccountID() || process.env.ACCOUNT_ID || null;

  /** Notifications surface for the platform we are running on. */
  const notificationsLocation = () =>
    Platform?.is?.mobile
      ? { name: "Notifications" }
      : { name: "Home", query: { openNotification: true } };

  /**
   * Coupons for a store. MyCoupons is a mobile-only screen; web shows the same
   * coupons on the store page's Coupons tab, matching seeCoupon() and the
   * Coupon Expiration branch.
   */
  const couponsLocation = (storeId) =>
    Platform?.is?.mobile
      ? { name: "MyCoupons", params: { storeid: storeId } }
      : {
          name: "StoreDetail",
          params: { storeid: storeId },
          query: { tab: "Coupons" },
        };

  /**
   * Maps a Klaviyo destination path onto a route. Self-contained on purpose:
   * push redirection owns its own URL handling and does not share the cordova
   * deep-link matcher, so changing one cannot silently change the other.
   *
   * Returns { location, storeId }, or null when the path says nothing useful.
   */
  function routeForKlaviyoPath(path) {
    const parts = (path || "").split("/").filter(Boolean);

    // /my-deals carries no store of its own — resolve it against the store this
    // build is for, and land on the same deals page a
    // /store-detail/:storeid/my-deal link would.
    if (parts[0] === "my-deals" && parts.length === 1) {
      const storeId = resolveKlaviyoStoreId();

      if (!storeId) return { location: { name: "MyDeals" }, storeId: null };

      return {
        location: { name: "MyDeal", params: { storeid: storeId } },
        storeId,
      };
    }

    // /coupons names no store of its own.
    if (parts[0] === "coupons" && parts.length === 1) {
      const storeId = resolveKlaviyoStoreId();

      // Web has its own store-agnostic coupons page, so go straight there.
      // Whatever store the user already had selected stays selected; we only
      // fill one in when there is none.
      if (!Platform?.is?.mobile) {
        return {
          location: { name: "Coupons" },
          storeId: commonStore.selectedStore?.id ? null : storeId,
        };
      }

      // Mobile coupons are per store, so one has to be resolvable.
      if (!storeId) {
        return { location: notificationsLocation(), storeId: null };
      }

      return { location: couponsLocation(storeId), storeId };
    }

    if (parts[0] !== "store-detail" || !parts[1]) return null;

    // /store-detail/:dealid/deal-detail/:type is keyed by DEAL id, not store
    // id, so parts[1] must not be read as a store here.
    if (parts[2] === "deal-detail") {
      return {
        location: {
          name: "DealDetail",
          params: { dealid: parts[1], type: parts[3] || "store" },
        },
        storeId: null,
      };
    }

    const storeId = parts[1];

    if (parts[2] === "my-deal") {
      return {
        location: { name: "MyDeal", params: { storeid: storeId } },
        storeId,
      };
    }

    if (parts[2] === "my-coupons") {
      return { location: couponsLocation(storeId), storeId };
    }

    return {
      location: { name: "StoreDetail", params: { storeid: storeId } },
      storeId,
    };
  }

  function redirectKlaviyoNotification(payload) {
    console.log("redirectKlaviyoNotification", payload);
    const path = extractPath(getKlaviyoUrl(payload));
    const target = routeForKlaviyoPath(path);

    if (target) {
      if (target.storeId) {
        commonStore.selectStoreForGlobal({ id: target.storeId });
      }
      go(target.location);
      return;
    }

    // No usable URL: fall back to the store surface when the build names one.
    const storeId = resolveKlaviyoStoreId();

    if (!storeId) {
      // Nothing to act on — open the notifications surface, same as an
      // unknown notification type does.
      go(notificationsLocation());
      return;
    }

    commonStore.selectStoreForGlobal({ id: storeId });
    go({ name: "StoreDetail", params: { storeid: storeId } });
  }

  function notificationRedirection() {
    const notiPayload = getPayload();
    const type = notiPayload?.type;

    // Nothing actionable — bail without touching navigation.
    if (!type) {
      clearPending();
      return;
    }

    commonStore.setLoader(true);
    loadingApi = true;

    if (type == "Customer Message") {
      setTimeout(() => {
        if (Platform.is.mobile && notiPayload.group_id) {
          go({ name: "Chat", params: { msgId: notiPayload.group_id } });
        } else {
          go({ name: "Messages" });
        }
      }, 2000);
    } else if (
      (type == "New Campaign" ||
        type == "Campaign Completion" ||
        type == "Campaign Ending" ||
        type == "Special Offers Available!") &&
      notiPayload.id
    ) {
      setTimeout(() => {
        const store_id =
          notiPayload?.account_id ||
          notiPayload?.store_id ||
          notiPayload?.id ||
          null;
        if (store_id) {
          if (type?.toLowerCase() == "special offers available!") {
            go({ name: "StoreDetail", params: { storeid: store_id } });
          } else {
            commonStore.selectStoreForGlobal({ id: store_id });
            go({
              name: "DealDetail",
              params: { dealid: notiPayload.id, type: "store" },
            });
          }
        } else {
          clearPending();
        }
      }, 2000);
    } else if (
      type == "Customer Receipt" &&
      notiPayload.id &&
      notiPayload.account_id
    ) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        go({ name: "ReceiptsDetail", params: { id: notiPayload.id } });
      }, 2000);
    } else if (type == "Friend Request" || type == "Team Request") {
      setTimeout(() => {
        go({ name: "AllRequests" });
      }, 2000);
    } else if (type == "Gift Received" && notiPayload?.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        if (Platform?.is?.mobile && notiPayload.id) {
          go({
            name: "GiftsDetail",
            params: { giftId: notiPayload.id },
            query: { category: "store", type: "received" },
          });
        } else {
          go({ name: "Gifts", query: { category: "store", type: "" } });
        }
      }, 2000);
    } else if (type == "Multiple New Campaign" && notiPayload?.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        if (Platform?.is?.mobile) {
          go({ name: "MyDeal", params: { storeid: notiPayload.account_id } });
        } else {
          go({ name: "StoreDetail", params: { storeid: notiPayload.account_id } });
        }
      }, 2000);
    } else if (type == "Team Points" || type == "Team Earnings") {
      // A teammate's purchase earned this customer points — open that store's earnings.
      const store_id = notiPayload?.account_id || notiPayload?.store_id || null;
      if (store_id) {
        commonStore.selectStoreForGlobal({ id: store_id });
      }
      setTimeout(() => {
        if (!store_id) {
          sendToHome();
        } else if (Platform?.is?.mobile) {
          go({
            name: "PointsBreakdownDetail",
            params: { storeid: store_id, type: "store" },
          });
        } else {
          go({ name: "MyProfile" });
        }
      }, 2000);
    } else if (type == "Store Feedback" && notiPayload?.account_id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        if (Platform?.is?.mobile) {
          go({
            name: "StoreFeedback",
            params: { storeid: notiPayload.account_id },
          });
        } else {
          go({
            name: "StoreDetail",
            params: { storeid: notiPayload.account_id },
            query: { tab: "Reviews" },
          });
        }
      }, 2000);
    } else if (
      (type == "Swag Order Arrived" || type == "Swag Order Picked Up") &&
      notiPayload?.id &&
      notiPayload?.account_id
    ) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        go({
          name: "HistoryDetail",
          params: { id: notiPayload.id },
          query: { category: "store" },
        });
      }, 2000);
    } else if (type == "Tokens Earned") {
      setTimeout(() => {
        redirectToTokenEarned(notiPayload);
      }, 2000);
    } else if (type == "Coupons Earned") {
      setTimeout(() => {
        seeCoupon(notiPayload);
      }, 2000);
    } else if (
      type == "Raffle Points Earned" &&
      notiPayload.id &&
      notiPayload.account_id
    ) {
      commonStore.selectStoreForGlobal({ id: notiPayload.account_id });
      setTimeout(() => {
        go({
          name: "DealDetail",
          params: { dealid: notiPayload.id, type: "store" },
        });
      }, 2000);
    } else if (
      (type == "Coupon Expiration" || type == "Custom Coupon") &&
      notiPayload?.id
    ) {
      commonStore.selectStoreForGlobal({ id: notiPayload.id });
      setTimeout(() => {
        if (Platform?.is?.mobile) {
          go({ name: "MyCoupons", params: { storeid: notiPayload.id } });
        } else {
          go({
            name: "StoreDetail",
            params: { storeid: notiPayload.id },
            query: { tab: "Coupons" },
          });
        }
      }, 2000);
    } else if (type?.toLowerCase() == "klaviyo notification") {
      setTimeout(() => {
        redirectKlaviyoNotification(notiPayload);
      }, 2000);
    } else if (type?.toLowerCase() == "online discount") {
      // Opens the discount in its own store. The store arrives as store_id or
      // account_id depending on the sender; without it or the discount_id
      // there is nothing to open, so fall back to the notifications surface.
      const storeId = notiPayload?.store_id || notiPayload?.account_id;
      const discountId = notiPayload?.discount_id;
      notificationStore.markOnlineDiscountPushRead(
        notiPayload?.process_id,
        storeId
      );

      setTimeout(() => {
        if (storeId && discountId) {
          commonStore.selectStoreForGlobal({ id: storeId });
          go({
            name: "OnlineDiscountDetail",
            params: { storeid: storeId, discountid: discountId },
          });
        } else if (Platform.is.mobile) {
          go({ name: "Notifications" });
        } else {
          go({ name: "Home", query: { openNotification: true } });
        }
      }, 2000);
    } else if (type?.toLowerCase() == "multiple online discount") {
      // Several discounts at once: open the store rather than one discount.
      const storeId =
        notiPayload?.store_id || notiPayload?.account_id || notiPayload?.id;
      notificationStore.markOnlineDiscountPushRead(
        notiPayload?.process_id,
        storeId
      );

      setTimeout(() => {
        if (storeId) {
          commonStore.selectStoreForGlobal({ id: storeId });
          go({ name: "StoreDetail", params: { storeid: storeId } });
        } else if (Platform.is.mobile) {
          go({ name: "Notifications" });
        } else {
          go({ name: "Home", query: { openNotification: true } });
        }
      }, 2000);
    } else if (type?.toLowerCase() == "pplus" && notiPayload?.id) {
      commonStore.selectStoreForGlobal({ id: notiPayload.id });
      setTimeout(() => {
        notificationStore.trackNotificationForTypePPlus(
          notiPayload?.offer_type,
          notiPayload?.offer_id
        );
        if (Platform?.is?.mobile) {
          go({ name: "MyDeal", params: { storeid: notiPayload.id } });
        } else {
          go({ name: "StoreDetail", params: { storeid: notiPayload.id } });
        }
      }, 2000);
    } else {
      // Unknown / non-deep-link type: open the notifications surface.
      setTimeout(() => {
        if (Platform.is.mobile) {
          go({ name: "Notifications" });
        } else {
          go({ name: "Home", query: { openNotification: true } });
        }
      }, 2000);
    }
  }

  return {
    isLoading,
    getPayload,
    setPayload,
    hasPending,
    trackOpen,
    clearPending,
    notificationRedirection,
  };
}
